/*
 * Copyright (c) 2026. Orion Analytics. All Rights Reserved
 */

/*
 * Copyright (c) 2026.
 * Orion Analytics.
 * All Rights Reserved
 */

import { useMemo, useState } from "react";
import Plot from "react-plotly.js";

export default function OIHeatmap({
    optionData = []
}) {

    const [optionType, setOptionType] =
        useState("CE");

    const filteredData =
        useMemo(() => {

            if (!optionData.length)
                return [];

            const expiries =
                [...new Set(
                    optionData.map(
                        d => d.expiry
                    )
                )]

                .sort(
                    (a, b) =>
                        new Date(a) -
                        new Date(b)
                )

                .slice(0, 3);

            return optionData.filter(
                d =>
                    d.option_type ===
                        optionType &&
                    expiries.includes(
                        d.expiry
                    )
            );

        }, [
            optionData,
            optionType
        ]);

    const strikes =
        useMemo(() => {

            return [
                ...new Set(
                    filteredData.map(
                        d => d.strike
                    )
                )
            ]

            .sort(
                (a, b) => a - b
            );

        }, [filteredData]);

    const expiries =
        useMemo(() => {

            return [
                ...new Set(
                    filteredData.map(
                        d => d.expiry
                    )
                )
            ];

        }, [filteredData]);

    const heatmapMatrix =
        useMemo(() => {

            return expiries.map(
                expiry => {

                    return strikes.map(
                        strike => {

                            const row =
                                filteredData.find(
                                    d =>
                                        d.expiry ===
                                            expiry &&
                                        d.strike ===
                                            strike
                                );

                            return row?.oi || 0;

                        }
                    );

                }
            );

        }, [
            filteredData,
            strikes,
            expiries
        ]);

    const totalOI =
        filteredData.reduce(
            (sum, d) =>
                sum + (d.oi || 0),
            0
        );

    const maxOI =
        Math.max(
            ...filteredData.map(
                d => d.oi || 0
            ),
            0
        );

    const maxOIRecord =
        filteredData.find(
            d => d.oi === maxOI
        );

    return (

        <div className="
            space-y-6
        ">

            {/* HEADER */}

            <div className="
                bg-slate-950
                border
                border-slate-800
                rounded-3xl
                p-6
            ">

                <div className="
                    flex
                    flex-col
                    lg:flex-row
                    lg:justify-between
                    gap-6
                ">

                    <div>

                        <h2 className="
                            text-xl
                            font-bold
                            text-white
                        ">
                            Open Interest Heatmap
                        </h2>

                        <p className="
                            text-slate-400
                            mt-1
                        ">
                            Strike-wise OI concentration
                            across expiries
                        </p>

                    </div>

                    <div className="
                        flex
                        gap-2
                    ">

                        <button
                            onClick={() =>
                                setOptionType(
                                    "CE"
                                )
                            }
                            className={`
                                px-4
                                py-2
                                rounded-xl

                                ${
                                    optionType ===
                                    "CE"
                                        ? "bg-emerald-500 text-white"
                                        : "bg-slate-900 text-slate-300"
                                }
                            `}
                        >
                            Calls
                        </button>

                        <button
                            onClick={() =>
                                setOptionType(
                                    "PE"
                                )
                            }
                            className={`
                                px-4
                                py-2
                                rounded-xl

                                ${
                                    optionType ===
                                    "PE"
                                        ? "bg-emerald-500 text-white"
                                        : "bg-slate-900 text-slate-300"
                                }
                            `}
                        >
                            Puts
                        </button>

                    </div>

                </div>

            </div>

            {/* KPI ROW */}

            <div className="
                grid
                grid-cols-1
                md:grid-cols-3
                gap-4
            ">

                <div className="
                    bg-slate-950
                    border
                    border-slate-800
                    rounded-2xl
                    p-4
                ">

                    <div className="
                        text-slate-400
                        text-xs
                    ">
                        Total OI
                    </div>

                    <div className="
                        text-2xl
                        font-bold
                        text-white
                        mt-2
                    ">
                        {totalOI.toLocaleString()}
                    </div>

                </div>

                <div className="
                    bg-slate-950
                    border
                    border-slate-800
                    rounded-2xl
                    p-4
                ">

                    <div className="
                        text-slate-400
                        text-xs
                    ">
                        Largest OI
                    </div>

                    <div className="
                        text-2xl
                        font-bold
                        text-white
                        mt-2
                    ">
                        {maxOI.toLocaleString()}
                    </div>

                </div>

                <div className="
                    bg-slate-950
                    border
                    border-slate-800
                    rounded-2xl
                    p-4
                ">

                    <div className="
                        text-slate-400
                        text-xs
                    ">
                        Max OI Strike
                    </div>

                    <div className="
                        text-2xl
                        font-bold
                        text-white
                        mt-2
                    ">
                        {maxOIRecord?.strike || "-"}
                    </div>

                </div>

            </div>

            {/* HEATMAP */}

            <div className="
                bg-slate-950
                border
                border-slate-800
                rounded-3xl
                p-4
            ">

                <Plot

                    data={[

                        {
                            z: heatmapMatrix,

                            x: strikes,

                            y: expiries,

                            type: "heatmap",

                            colorscale:
                                "Viridis",

                            hovertemplate:
                                "Expiry: %{y}<br>" +
                                "Strike: %{x}<br>" +
                                "OI: %{z:,.0f}" +
                                "<extra></extra>"
                        }

                    ]}

                    layout={{

                        height: 650,

                        paper_bgcolor:
                            "#020617",

                        plot_bgcolor:
                            "#020617",

                        font: {
                            color:
                                "#cbd5e1"
                        },

                        margin: {
                            l: 80,
                            r: 20,
                            t: 20,
                            b: 60
                        },

                        xaxis: {
                            title:
                                "Strike"
                        },

                        yaxis: {
                            title:
                                "Expiry"
                        }
                    }}

                    config={{
                        responsive:
                            true,

                        displayModeBar:
                            false
                    }}

                    style={{
                        width: "100%"
                    }}

                />

            </div>

        </div>

    );

}